import Component from '@glimmer/component';
import AppHeaderInitialsIcon from 'tio-employee/components/app/header/initials-icon';
import { service } from '@ember/service';
import Popover from 'tio-reimagined/components/popover';
import { PhCaretRight, PhSignOut, PhUserCircle } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import { concat } from '@ember/helper';
import { eq, lowercase } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import type SessionContextService from 'tio-employee/services/session-context';
import type RoleViewModel from 'tio-common/models/role-view';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import { LinkTo } from '@ember/routing';

export default class SidebarUserMenuComponent extends Component {
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @tracked currentRole?: RoleViewModel;

  constructor(owner: Owner, args: never) {
    super(owner, args);
    this.currentRole = this.sessionContext.currentRole!;
  }

  get activeRoles() {
    return this.sessionContext.roles.filter((role) => role.isActive);
  }

  get user() {
    return this.sessionContext.user;
  }

  @tracked isMenuOpen = false;

  toggleMenu = () => {
    this.isMenuOpen = !this.isMenuOpen;
  };

  closeMenu = () => {
    this.isMenuOpen = false;
  };

  handleSetCurrentRole = (role: RoleViewModel) => {
    this.isMenuOpen = false;
    this.setCurrentRole(role);
  };

  handleLogout = () => {
    this.isMenuOpen = false;
    this.router.transitionTo('logout');
  };

  setCurrentRole = (role: RoleViewModel) => {
    this.sessionContext.setCurrentRole(role);
    this.currentRole = role;
  };

  get isFamilyMember() {
    return this.sessionContext.isFamilyMember;
  }

  <template>
    {{! Mobile (show static list instead of dropdown) }}
    <div class="lg:hidden mb-4">
      <div class="mb-2 w-full text-sm text-white text-left px-1 py-1">
        <div class="flex items-center font-normal">
          <AppHeaderInitialsIcon @initials={{this.user.person.initials}} @size="sm" />
          <div class="align-middle ml-2 max-w-[140px] truncate">{{this.user.person.firstName}}
            {{this.user.person.lastName}}</div>
        </div>
      </div>
      <nav class="flex flex-col space-y-1 text-sm font-medium">
        <LinkTo
          @route="authenticated.profile"
          class="flex items-center px-2 py-1 rounded text-white/90 hover:bg-gray-50 hover:text-midnight transition"
        >
          <PhUserCircle @size="16" class="mr-2" />
          {{t "sidebar.account_information"}}
        </LinkTo>
        {{#unless this.isFamilyMember}}
          {{#each this.activeRoles as |role|}}
            <button
              type="button"
              class="flex items-center text-left px-2 py-1 rounded transition
                {{if
                  (eq this.currentRole.relationshipType role.relationshipType)
                  'bg-white/10 text-white'
                  'text-white/80 hover:bg-gray-50 hover:text-midnight'
                }}"
              {{on "click" (fn this.setCurrentRole role)}}
            >
              {{#if (eq this.currentRole.relationshipType role.relationshipType)}}
                <PhCaretRight @size="16" class="mr-2" />
              {{else}}
                <div class="w-4 h-4 mr-2"></div>
              {{/if}}
              {{t (concat "user-roles." (lowercase role.relationshipType))}}
            </button>
          {{/each}}
        {{/unless}}
        <LinkTo
          @route="logout"
          class="flex items-center px-2 py-1 rounded text-white/90 hover:bg-gray-50 hover:text-midnight transition"
        >
          <PhSignOut @size="16" class="mr-2" />
          {{t "header.user_menu.logout"}}
        </LinkTo>
      </nav>
    </div>

    {{! Desktop / larger screens: user menu in Popover }}
    <div class="hidden lg:block">
      <Popover @isOpen={{this.isMenuOpen}} @onClose={{this.closeMenu}} @placement="top">
        <:trigger as |p|>
          <button
            type="button"
            class="mb-2 w-full text-sm text-white text-left hover:bg-gray-50 hover:text-midnight px-1 py-1 rounded"
            {{p.register}}
            {{on "click" this.toggleMenu}}
          >
            <div class="flex items-center font-normal">
              <AppHeaderInitialsIcon @initials={{this.user.person.initials}} @size="sm" />
              <div class="align-middle ml-2 max-w-[140px] truncate">{{this.user.person.firstName}}
                {{this.user.person.lastName}}</div>
            </div>
          </button>
        </:trigger>
        <:body>
          <div class="flex flex-col min-w-48 text-gray-900">
            <LinkTo
              @route="authenticated.profile"
              class="flex items-center px-3 py-2 text-sm hover:bg-gray-100 rounded"
              {{on "click" this.closeMenu}}
            >
              <PhUserCircle @size="16" class="mr-2" />
              {{t "sidebar.account_information"}}
            </LinkTo>
            {{#unless this.isFamilyMember}}
              {{#each this.activeRoles as |role|}}
                <button
                  type="button"
                  class="flex items-center w-full px-3 py-2 text-sm hover:bg-gray-100 rounded"
                  {{on "click" (fn this.handleSetCurrentRole role)}}
                >
                  {{#if (eq this.currentRole.relationshipType role.relationshipType)}}
                    <PhCaretRight @size="16" class="mr-2" />
                  {{else}}
                    <div class="w-4 h-4 mr-2"></div>
                  {{/if}}
                  {{t (concat "user-roles." (lowercase role.relationshipType))}}
                </button>
              {{/each}}
            {{/unless}}
            <div class="border-t border-gray-200 my-1"></div>
            <button
              type="button"
              class="flex items-center w-full px-3 py-2 text-sm hover:bg-gray-100 rounded"
              {{on "click" this.handleLogout}}
            >
              <PhSignOut @size="16" class="mr-2" />
              {{t "header.user_menu.logout"}}
            </button>
          </div>
        </:body>
      </Popover>
    </div>
  </template>
}
